import {ComponentType, ReactNode, useCallback} from 'react';

import type {Analytics, Client} from '@stripe-internal/conversations-client';
import {
  AnalyticsProvider,
  ClientProvider,
  ParentViewportProvider,
  WidgetAPI,
  WidgetProvider,
} from '@stripe-internal/conversations-ui';
import type {VisualViewportSnapshot} from '@stripe-internal/support-conversations-controller';

import type {AppConfig} from '../types';
import {FileUploadProvider} from './FileUploadProvider';
import {useApp, useConfig, useParentViewportSnapshot} from '../hooks/appHooks';

type ConversationsUIProvidersBaseProps = {
  children: ReactNode;
  analytics: Analytics;
  client?: Client | null;
  config: AppConfig;
  actionHandler: WidgetAPI['requestAction'];
  FileUploadProviderComponent: ComponentType<{children: ReactNode}>;
  parentViewportSnapshot?: VisualViewportSnapshot;
};

export function ConversationsUIProvidersBase({
  children,
  analytics,
  client,
  config,
  actionHandler,
  FileUploadProviderComponent,
  parentViewportSnapshot,
}: ConversationsUIProvidersBaseProps) {
  const widgetProvider = (
    <FileUploadProviderComponent>
      <WidgetProvider
        initialVisibility={config.initialVisibility}
        audience={config.audience}
        entryPoint={config.entryPoint}
        parentOrigin={config.parentOrigin}
        // TODO: will need to update lingering references to widget.requestAction
        // to not reference the widget.
        // That and a few dot_com specific widget visibility references
        // are what's remaining, before we could get rid of this provider
        // for the extracted assistant
        actionHandler={actionHandler}
        // note we specifically do not pass a parentMessageBus to the
        // widget provider as we want all message handling done by the
        // assistant now
      >
        {children}
      </WidgetProvider>
    </FileUploadProviderComponent>
  );

  return (
    <ParentViewportProvider value={parentViewportSnapshot}>
      <AnalyticsProvider analytics={analytics}>
        {client ? (
          <ClientProvider value={client}>{widgetProvider}</ClientProvider>
        ) : (
          widgetProvider
        )}
      </AnalyticsProvider>
    </ParentViewportProvider>
  );
}

export function ConversationsUIProviders({children}: {children: ReactNode}) {
  const app = useApp();
  const config = useConfig();
  const parentViewportSnapshot = useParentViewportSnapshot();

  const actionHandler = useCallback<WidgetAPI['requestAction']>(
    (action, opts) => app.requestAction({action, ...opts}),
    [app],
  );

  return (
    <ConversationsUIProvidersBase
      analytics={app.analytics}
      client={app.client}
      config={config}
      actionHandler={actionHandler}
      FileUploadProviderComponent={FileUploadProvider}
      parentViewportSnapshot={parentViewportSnapshot}
    >
      {children}
    </ConversationsUIProvidersBase>
  );
}
